import { FileDown, Loader2, Plus, Printer, Share2, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { CarregandoGrade } from "@/components/carregando";

import { mandarParaImpressora } from "@/features/mesa-qr/imprimirNaTermica";

import { baixarPdfLinks, divulgacaoApi, imprimirLinks } from "./api";
import CardLink, { CardCadastrar } from "./CardLink";
import { ModalDeletarLink, ModalNovoLink } from "./ModaisLinks";
import ModalMetricasLink from "./ModalMetricasLink";
import ModalQrLink from "./ModalQrLink";
import ParametrosMeta from "./ParametrosMeta";

const MAX_REDES_CUSTOM = 2; // espelho do backend

/**
 * IMPULSIONE -> Links de divulgacao: um link FIXO e rastreavel por plataforma,
 * mais redes extras com nome livre (max 2) e links personalizados por
 * INFLUENCER (deletaveis). Metricas sao POR CARD (pedido do dono) — cliques,
 * pedidos e faturado de cada link, com a data de criacao.
 */
export default function LinksDivulgacaoPage() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [gerando, setGerando] = useState(null);       // slug de plataforma em criacao
  const [copiado, setCopiado] = useState(null);       // token copiado (feedback 2s)
  const [modalNovo, setModalNovo] = useState(null);   // 'rede' | 'influencer'
  const [deletando, setDeletando] = useState(null);   // link de influencer a confirmar
  const [verMetricas, setVerMetricas] = useState(null); // {link, tile, titulo} do popup de graficos
  const [verQr, setVerQr] = useState(null);           // {link, titulo} da janela do QR Code
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);

  const carregar = () =>
    divulgacaoApi.listar().then(setDados)
      .catch(() => setErro("Não foi possível carregar os links."));
  useEffect(() => { carregar(); }, []);

  // QR Codes de todos os links num PDF só (cartões de 4 por folha A4)
  const baixarTodos = async () => {
    setBaixandoPdf(true);
    try { await baixarPdfLinks(null); }
    catch { toast.error("Não consegui gerar o PDF."); }
    finally { setBaixandoPdf(false); }
  };
  const imprimirTodos = async () => {
    setImprimindo(true);
    await mandarParaImpressora(() => imprimirLinks(null));
    setImprimindo(false);
  };
  const temLink = !!(dados?.links || []).length;

  // Título aparece na hora; só os cards esperam os dados.
  const cabecalho = (
    <div>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Share2 className="w-6 h-6 text-primary" />
        <h1 className="text-2xl sm:text-3xl font-bold">Links de divulgação</h1>
        {temLink && (
          <div className="ml-auto flex flex-wrap gap-2">
            <button type="button" onClick={imprimirTodos} disabled={imprimindo} data-e2e="qr-todos-links-imprimir"
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium hover:border-primary transition-colors disabled:opacity-50">
              {imprimindo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              Imprimir todos os QR Codes
            </button>
            <button type="button" onClick={baixarTodos} disabled={baixandoPdf} data-e2e="qr-todos-links-pdf"
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium hover:border-primary transition-colors disabled:opacity-50">
              {baixandoPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              QR Codes de todos os links (PDF)
            </button>
          </div>
        )}
      </div>
      <p className="text-muted-foreground max-w-2xl">
        Um link fixo e rastreável para cada rede. Cole na bio, nos anúncios e nas
        descrições — cada card mostra os cliques, pedidos e faturamento do próprio link.
      </p>
    </div>
  );
  if (erro || !dados) {
    return (
      <div className="p-4 sm:p-6 space-y-5 w-full">
        {cabecalho}
        {erro ? <p className="text-destructive">{erro}</p> : (
          // Carregamento com cards (25/10/2026): os 8 cards das redes piscando.
          <CarregandoGrade quantos={8} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" altura="h-[120px]" />
        )}
      </div>
    );
  }

  const links = dados.links || [];
  const porSlug = Object.fromEntries(links.map((l) => [l.plataforma, l]));
  const redesCustom = links.filter((l) => l.tipo === "rede");
  const influencers = links.filter((l) => l.tipo === "influencer");

  const gerar = async (slug) => {
    setGerando(slug);
    try { await divulgacaoApi.gerar(slug); await carregar(); }
    catch { toast.error("Não foi possível gerar o link — tente de novo."); }
    finally { setGerando(null); }
  };

  const criarCustom = async (nome) => {
    try { await divulgacaoApi.criarCustom(nome, modalNovo); await carregar(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível criar o link."); throw e; }
  };

  const deletar = async () => {
    try { await divulgacaoApi.deletar(deletando.track_token); await carregar(); toast.success(`Link de ${deletando.nome} deletado.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível deletar."); throw e; }
  };

  const copiar = (l) => {
    navigator.clipboard?.writeText(`https://${l.link}`);
    setCopiado(l.track_token);
    setTimeout(() => setCopiado(null), 2000);
  };

  const cardDe = (l, extra = {}) => (
    <CardLink key={l.track_token} link={l} onCopiar={() => copiar(l)}
      copiado={copiado === l.track_token}
      onMetricas={() => setVerMetricas({ link: l, tile: extra.tile, titulo: extra.titulo })}
      onQr={() => setVerQr({ link: l, titulo: extra.titulo })}
      {...extra} />
  );

  return (
    <div className="p-4 sm:p-6 space-y-5 w-full">
      {cabecalho}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Object.entries(dados.plataformas).map(([slug, nome]) => {
          const l = porSlug[slug];
          if (l) return cardDe(l, { tile: slug, titulo: nome });
          return (
            <div key={slug} className="rounded-xl border bg-card p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <CardLink.Tile slug={slug} />
                <p className="font-semibold">{nome}</p>
              </div>
              <button onClick={() => gerar(slug)} disabled={gerando === slug}
                className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-sm font-medium hover:border-primary transition-colors disabled:opacity-50">
                <Plus className="w-4 h-4" />
                {gerando === slug ? "Gerando..." : "Gerar link"}
              </button>
            </div>
          );
        })}
        {redesCustom.map((l) => cardDe(l, { tile: "rede", titulo: l.nome }))}
        {redesCustom.length < MAX_REDES_CUSTOM && (
          <CardCadastrar rotulo="Cadastrar outra rede" onClick={() => setModalNovo("rede")} />
        )}
      </div>

      {/* So' aparece depois que o link "Anuncios Meta" foi gerado. */}
      <ParametrosMeta link={porSlug.meta_ads} />

      <div className="pt-2">
        <div className="flex items-center gap-2 mb-1">
          <UserRound className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-bold">Influencers</h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl mb-4">
          Um link exclusivo para cada influencer que divulga sua loja — você vê exatamente
          quantos cliques, pedidos e faturamento cada um trouxe.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {influencers.map((l) => cardDe(l, {
            tile: "influencer", titulo: l.nome, onDeletar: () => setDeletando(l),
          }))}
          <CardCadastrar rotulo="Cadastrar influencer" onClick={() => setModalNovo("influencer")} />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Como funciona: o link abre o seu cardápio e marca o navegador do cliente por 7 dias —
        pedido feito nesse prazo conta pra origem. Cliques incluem visitas repetidas.
      </p>

      <ModalNovoLink
        aberto={modalNovo === "rede"}
        titulo="Cadastrar outra rede"
        descricao={`Dê um nome pra rede (ex.: Pinterest, X, Telegram). Você pode ter até ${MAX_REDES_CUSTOM} redes além das fixas.`}
        placeholder="Nome da rede"
        onFechar={() => setModalNovo(null)}
        onCriar={criarCustom}
      />
      <ModalNovoLink
        aberto={modalNovo === "influencer"}
        titulo="Cadastrar influencer"
        descricao="Dê o nome do influencer — o link gerado é exclusivo dele, e as métricas mostram o que ele trouxe."
        placeholder="Nome do influencer"
        onFechar={() => setModalNovo(null)}
        onCriar={criarCustom}
      />
      <ModalDeletarLink link={deletando} onFechar={() => setDeletando(null)} onDeletar={deletar} />
      <ModalMetricasLink link={verMetricas?.link || null} tile={verMetricas?.tile}
        titulo={verMetricas?.titulo} onFechar={() => setVerMetricas(null)} />
      <ModalQrLink link={verQr?.link || null} titulo={verQr?.titulo} onFechar={() => setVerQr(null)} />
    </div>
  );
}
